// v2.18.0 — Route livreur protégée : clone fonctionnel de /navigate avec badge PRO.
// v2.18.4a — Refonte mobile-premium : suppression de la barre PRO horizontale,
//            remplacée par un burger avatar 44×44 + drawer slide-in droite.

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import DeliveryPage from "@/pages/DeliveryPage";
import CourseControls from "@/components/CourseControls";
import WeatherBadge from "@/components/WeatherBadge";
import MultiStopsPanel from "@/components/MultiStopsPanel";
import CourierProDrawer, { CourierProBurger } from "@/components/CourierProDrawer";
import TrafficGuardian from "@/components/TrafficGuardian";
import CourierOffersWatch from "@/components/CourierOffersWatch";

// v3.6.5 — Bouton "Démo livraison" : auto-play d'une livraison simulée depuis
// Boutique Awa Cosmétiques (Cocody) vers un client Riviera. Masqué si course
// active ou déjà en simulation.
const DEMO_SIM_URL = "/livreur/navigate?from=5.3599,-3.9910&dest=5.3721,-3.9481"
  + "&code=CIV-COC-RIV-DEMO1&label=" + encodeURIComponent("Client Riviera — commande Boutique Awa Cosmétiques") + "&sim=1";

function DemoSimButton() {
  const [courseActive, setCourseActive] = useState(false);
  const [isSim] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("sim") === "1"; } catch (_) { return false; }
  });
  useEffect(() => {
    const onCourse = (e) => setCourseActive(!!e?.detail?.active);
    window.addEventListener("civ-courier-course-changed", onCourse);
    return () => window.removeEventListener("civ-courier-course-changed", onCourse);
  }, []);
  if (courseActive || isSim) return null;
  return (
    <button
      type="button"
      data-testid="demo-sim-btn"
      onClick={() => { window.location.href = DEMO_SIM_URL; }}
      style={{
        position: "fixed", top: 60, right: 12, zIndex: 55,
        display: "flex", alignItems: "center", gap: 7,
        background: "linear-gradient(180deg, rgba(13,18,22,0.92), rgba(5,9,11,0.96))",
        border: "1px solid rgba(0,255,136,0.4)",
        color: "#00FF88",
        padding: "9px 14px", borderRadius: 999,
        fontSize: 12, fontWeight: 800, cursor: "pointer",
        boxShadow: "0 8px 24px rgba(0,0,0,0.5), 0 0 14px rgba(0,255,136,0.15)",
        backdropFilter: "blur(10px)",
      }}
    >▶ Démo livraison Awa</button>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const NIGHT_KEY = "civ_courier_night_mode";

export default function CourierNavigatePage() {
  const [courier, setCourier] = useState(null);
  const [status, setStatus] = useState("checking");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [nightMode, setNightMode] = useState(() => {
    try { return typeof window !== "undefined" && window.localStorage.getItem(NIGHT_KEY) === "1"; }
    catch (_) { return false; }
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/v3/courier/me`, { withCredentials: true });
        if (r.data?.ok && r.data.courier) {
          setCourier(r.data.courier);
          setStatus("authorized");
        } else { setStatus("denied"); }
      } catch (_) { setStatus("denied"); }
    })();
  }, []);

  useEffect(() => {
    if (status === "denied") {
      const next = encodeURIComponent("/livreur/navigate" + window.location.search);
      window.location.replace(`/livreur/login?next=${next}`);
    }
  }, [status]);

  const onLogout = useCallback(async () => {
    try { await axios.post(`${API}/v3/courier/logout`, {}, { withCredentials: true }); } catch (_) {}
    window.location.replace("/livreur/login");
  }, []);

  const onToggleNightMode = useCallback(() => {
    const next = !nightMode;
    try { localStorage.setItem(NIGHT_KEY, next ? "1" : "0"); } catch (_) {}
    setNightMode(next);
    // Reload pour appliquer le style Mapbox.
    setTimeout(() => window.location.reload(), 150);
  }, [nightMode]);

  if (status === "checking") {
    return <div style={loadingStyles.bg}><div style={loadingStyles.spinner}><span style={{ fontSize: 22 }}>🛵</span><span style={loadingStyles.txt}>Vérification de l'accès Pro…</span></div></div>;
  }
  if (status !== "authorized") {
    return <div style={loadingStyles.bg}><div style={loadingStyles.txt}>Redirection vers la connexion…</div></div>;
  }

  return (
    <div data-testid="courier-navigate-page" style={{ position: "relative", width: "100%", height: "100vh" }}>
      <DeliveryPage />

      {/* v2.18.4a — Burger 44×44 (top-right, z-90) + drawer (z-80/81). */}
      <CourierProBurger open={drawerOpen} onClick={() => setDrawerOpen((v) => !v)} courier={courier} />
      <CourierProDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        courier={courier}
        nightMode={nightMode}
        onToggleNightMode={onToggleNightMode}
        onLogout={onLogout}
      />

      {/* Compatibilité tests v2.18.0+ : pro-overlay caché (1×1 px) avec testid + nom — non visible. */}
      <div data-testid="courier-pro-overlay" aria-hidden="true" style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }}>
        <span data-testid="courier-name-hidden">{courier?.courier_name}</span>
      </div>

      {/* Overlays existants (météo + multi-stops + course controls). */}
      <WeatherBadge />
      {/* v2.19.34a — Bouton "Retour au chat" en top-left (priorité navigation). */}
      <a
        href="/"
        data-testid="courier-back-to-chat"
        aria-label="Retour au chat AYA"
        style={{
          position: "fixed", top: 12, left: 12, zIndex: 40,
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "7px 12px",
          background: "rgba(15, 23, 42, 0.85)",
          border: "1px solid rgba(251, 191, 36, 0.35)",
          borderRadius: 999,
          backdropFilter: "blur(12px)",
          boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
          color: "#FBBF24", fontSize: 12, fontWeight: 700,
          textDecoration: "none",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        <span aria-hidden="true">←</span>
        <span>Chat</span>
      </a>
      <MultiStopsPanel />
      <CourseControls />
      {/* v2.19.35b — Bouton bascule mode 3D Live Nav ↔ Vue plan (overlay top-center). */}
      <LiveNavToggleBtn />
      {/* v2.19.37 — CTA "Confirmer arrivée" affiché quand aya:nav:end est dispatché. */}
      <ConfirmArrivalCTA />
      {/* v2.18.5 — Guardian anti-embouteillage (étape 6/6 finale tier PRO). */}
      <TrafficGuardian />
      {/* v2.19.35 — Marketplace : watcher offres + modal acceptance. */}
      <CourierOffersWatch />
      <DemoSimButton />
    </div>
  );
}

// v2.19.37 — CTA "Confirmer arrivée" bottom-sheet, visible quand le live nav termine.
// Récupère la course active du livreur depuis /my-courses puis navigue vers
// /courier/arrival/:order_ref. Bouton se masque automatiquement au click.
function ConfirmArrivalCTA() {
  const [arrived, setArrived] = useState(false);
  useEffect(() => {
    const onEnd = () => setArrived(true);
    window.addEventListener("aya:nav:end", onEnd);
    return () => window.removeEventListener("aya:nav:end", onEnd);
  }, []);
  if (!arrived) return null;
  const goArrival = async () => {
    // v3.20.0 — Mode démo (sim=1) : enchaîne sur la preuve de livraison DEMO
    // (PIN + photo + pourboire) au lieu de chercher une vraie course.
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("sim") === "1") {
        window.location.href = "/courier/arrival/DEMO-AWA-1";
        return;
      }
    } catch (_) { /* noop */ }
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/v3/delivery/my-courses`, { credentials: "include" });
      const d = await r.json();
      const active = (d?.courses || []).find((c) => ["accepted", "picked_up", "delivering"].includes(c.status));
      if (active) window.location.href = `/courier/arrival/${active.order_ref}`;
      else setArrived(false);
    } catch (_) { setArrived(false); }
  };
  return (
    <div data-testid="confirm-arrival-cta" style={{
      position: "fixed", bottom: 128, left: 16, right: 16, zIndex: 60,
      background: "linear-gradient(135deg, #16A34A, #22C55E)",
      color: "#0F172A", padding: "16px 22px", borderRadius: 14, fontWeight: 800, fontSize: 15,
      cursor: "pointer", boxShadow: "0 -4px 20px rgba(34,197,94,0.45), 0 4px 14px rgba(0,0,0,0.3)",
      textAlign: "center", animation: "slideUp 0.3s ease-out",
    }} onClick={goArrival}>
      📍 Arrivé à destination — ✅ Confirmer arrivée
    </div>
  );
}

// v2.19.35b — Bouton overlay : visible uniquement quand une course est active.
// Bascule mode 3D Live Navigation immersif (pitch 70, zoom 18.5, follow) ↔ Vue plan.
function LiveNavToggleBtn() {
  const [active, setActive] = useState(false);
  const [liveOn, setLiveOn] = useState(true);
  useEffect(() => {
    const onChange = (e) => {
      const isActive = !!e?.detail?.active;
      setActive(isActive);
      setLiveOn(isActive);
    };
    window.addEventListener("civ-courier-course-changed", onChange);
    return () => window.removeEventListener("civ-courier-course-changed", onChange);
  }, []);
  if (!active) return null;
  const toggle = () => {
    setLiveOn((v) => !v);
    window.dispatchEvent(new CustomEvent("aya-live-nav-toggle"));
  };
  return (
    <button
      onClick={toggle}
      data-testid="live-nav-toggle-btn"
      aria-label={liveOn ? "Vue plan" : "Vue immersive 3D"}
      style={{
        position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 41,
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "7px 14px",
        background: "rgba(15, 23, 42, 0.85)",
        border: `1px solid ${liveOn ? "rgba(34,197,94,0.5)" : "rgba(251,191,36,0.5)"}`,
        borderRadius: 999,
        backdropFilter: "blur(12px)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
        color: liveOn ? "#86EFAC" : "#FBBF24",
        fontSize: 12, fontWeight: 700,
        cursor: "pointer",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      {liveOn ? "🔙 Vue plan" : "🧭 Mode immersif 3D"}
    </button>
  );
}

const loadingStyles = {
  bg: {
    minHeight: "100vh", width: "100%",
    background: "#0F172A", color: "#E2E8F0",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  spinner: { display: "flex", alignItems: "center", gap: 10 },
  txt: { fontSize: 14, color: "#94A3B8" },
};
